import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { ICONS } from 'shared/constants/images';
import { Icon } from 'shared/ui/icon/icon';
import cls from './score-disclosure.module.scss';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { ScoreSquare } from 'shared/ui/score-square/score-square';
import { AnalysisRuleItem } from './components/rules/analysis-rule-item';
import { RuleDescription } from './components/rules/rule-description';
import { Tooltip } from 'shared/ui/icon-tooltip/icon-tooltip';
import { LLMSInsights } from 'components/llms-insights/llms-insights';
import { ExpiredRules, TitleMapKey } from './components/rules/expired-rules';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { ScoreDisclosureProps } from './score-disclosuere.types';
import { getPagePrefix, track } from 'utils/mixpanel';
import { getTrackName } from './helpers';
import { Chip } from 'shared/ui/chip/chip';
import { TrialBanner } from 'shared/ui/trial-banner/trial-banner';

export const ScoreDisclosure: React.FC<ScoreDisclosureProps> = (
  props: ScoreDisclosureProps
) => {
  const {
    title,
    score,
    description,
    scoring,
    confidence,
    evidence,
    extraction,
    identified,
    urlsAnalyzed,
    isLoading = false,
    className,
    buttonClassName,
    contentClassName,
    skipRestrictions = false,
    variant = 'info-completeness',
    ruleDescription,
  } = props;
  const { t } = useTranslation();
  const { isTrialExpired, isTrial, testingB } = useCachedSelectedProject();
  const scoresArray = Object.entries(scoring || {});
  const hasHiddenRulesForTrial =
    isTrial &&
    testingB &&
    !isTrialExpired &&
    !skipRestrictions &&
    scoresArray.length > 1;
  const trackName = getTrackName(title);
  const pagePrefix = getPagePrefix(window.location.pathname);
  const isTechnicalAccessibility = variant === 'technical-accessibility';
  const trackClick = (open: boolean) => {
    if (open) {
      track(`AIS:${pagePrefix}_COMPLETENESS-SECTION-COLAPSED`, {
        is_trial: isTrial,
        type: trackName,
      });
    } else {
      track(`AIS:${pagePrefix}_COMPLETENESS-SECTION-EXPANDED`, {
        is_trial: isTrial,
        type: trackName,
      });
    }
  };

  return (
    <Disclosure as='div' className={`${cls.disclosure} ${className || ''}`}>
      {({ open }) => (
        <>
          <DisclosureButton
            className={`${cls.button} ${buttonClassName || ''}`}
            onClick={() => trackClick(open)}
          >
            <div className={cls.text}>
              <h3>{t(title)}</h3>
              <p>{description}</p>
            </div>
            <div className={cls.score}>
              {isLoading ? (
                <Skeleton height={40} width={40} />
              ) : score !== undefined ? (
                <ScoreSquare score={score} isLocked={isTrialExpired} />
              ) : null}
              <Icon
                pathToIcon={ICONS.arrowUp}
                className={`${cls.icon} ${open ? cls.open : ''}`}
                size={20}
              />
            </div>
          </DisclosureButton>
          <DisclosurePanel
            as='div'
            className={`${cls.content} ${contentClassName || ''}`}
          >
            {isTechnicalAccessibility ? (
              <>
                <p className={cls.ruleDescriptionText}>{ruleDescription}</p>
                {scoresArray.length > 0 ? (
                  <h4 className={cls.ruleBasedTitle}>
                    {t('ruleBasedEvaluation')}
                  </h4>
                ) : !isLoading && !isTrialExpired ? (
                  <Chip color='gray'>{t('analysisBeAvailableSoon')}</Chip>
                ) : null}
              </>
            ) : (
              <LLMSInsights
                title={title}
                identified={identified}
                urlsAnalyzed={urlsAnalyzed}
                extraction={extraction}
                confidence={confidence}
                evidence={evidence}
                isLoading={isLoading}
              />
            )}
            <div className={cls.analysisCards}>
              {isTrialExpired ? (
                <ExpiredRules title={title as TitleMapKey} />
              ) : isLoading ? (
                <>
                  <Skeleton height={135} />
                  <Skeleton height={135} />
                  <Skeleton height={135} />
                </>
              ) : (
                scoresArray.map(([key, value], index) => (
                  <AnalysisRuleItem
                    trackName={trackName}
                    rule={value}
                    key={key}
                    title={key}
                    index={index}
                    skipRestrictions={skipRestrictions}
                    isTechnicalAccessibility={isTechnicalAccessibility}
                    maxScore={value.max_score}
                    ruleDescription={
                      <RuleDescription
                        trackName={trackName}
                        parentRuleTitle={title}
                        ruleTitle={key}
                      />
                    }
                  />
                ))
              )}
            </div>
            {hasHiddenRulesForTrial && (
              <TrialBanner
                className={cls.trialBanner}
                title={t('trialRulesBanner')}
                description={t('trialImprovementCenterDescription')}
              />
            )}
          </DisclosurePanel>

          {!isTechnicalAccessibility && (
            <Tooltip id='confidence-tt'>
              <span>
                <b>{t('confidence')}</b>: {t('confidenceTooltip')}
              </span>
              <br />
              <br />
              <span>
                <b>{t('evidence')}</b>: {t('evidenceTooltip')}
              </span>
            </Tooltip>
          )}
        </>
      )}
    </Disclosure>
  );
};
